// --- Our Modules --------------------------------------------------------------------------------
import useAuth from "@/hooks/useAuth";
import useFetch from "@/hooks/useFetch";
import usePopup from "@/hooks/usePopup";
import useTranslation from "@/hooks/useTranslation";
import ConfirmPopup from "@/UI/popups/ConfirmPopup";
import "./styles.css";

// --- Types --------------------------------------------------------------------------------------

type Props = {
    onClose: () => void;
};

// --- Logic --------------------------------------------------------------------------------------

export default function ProfilePopup({ onClose }: Props) {
    const t = useTranslation();
    const { addComp, removeComp } = usePopup();

    const admin = useAuth((state) => state.admin);
    const clearAuth = useAuth((state) => state.clearAuth);

    const { invoke: logoutInvoke } = useFetch("auth/logout", { method: "DELETE" }, false);

    const handleLogout = () => {
        onClose();
        removeComp("confirm-logout");
        addComp({
            name: "confirm-logout",
            Component: ConfirmPopup,
            place: "center",
            anim: "scale",
            cover: "shaded",
            coverClose: true,
            details: {
                message: t.profilePopup.logoutConfirmMessage,
                icon: "/icons/logout-icon.svg",
                variant: "danger",
                confirmLabel: t.profilePopup.logoutConfirmAction,
                cancelLabel: t.profilePopup.logoutCancel,
                onConfirm: () => {
                    // Fired without awaiting — the local session is dropped either way, and a failed
                    // call would otherwise strand the admin signed in on a device they meant to leave.
                    logoutInvoke();
                    clearAuth();
                    window.location.replace("/login");
                },
            },
        });
    };

    return (
        <div className="profile-popup">
            <div className="profile-popup-active">
                <div className="profile-popup-active-avatar">
                    {admin?.picture ? (
                        <img src={admin.picture} alt={admin.name} />
                    ) : (
                        <img className="profile-popup-avatar-placeholder" src="/icons/client-small-placeholder-icon.svg" alt={admin?.name ?? ""} />
                    )}
                </div>
                <div className="profile-popup-active-info">
                    <p className="profile-popup-name">{admin?.name ?? "—"}</p>
                    <p className="profile-popup-phone" dir="ltr">
                        {admin?.phone ?? ""}
                    </p>
                    <span className={`profile-popup-role${admin?.isSuper ? "" : " profile-popup-role--staff"}`}>
                        {admin?.isSuper ? t.profilePopup.superAdmin : t.profilePopup.admin}
                    </span>
                </div>
            </div>

            <div className="profile-popup-divider" />

            <button type="button" className="profile-popup-logout" onClick={handleLogout}>
                <img src="/icons/logout-icon.svg" alt="" width={14} height={14} />
                <span>{t.profilePopup.logout}</span>
            </button>
        </div>
    );
}
